<template>
    <div class="block-editor create-task">
        <div class="wrapper">
            <!-- Workflow Seçimi -->
            <section>
                <div class="title">İş Akışı</div>
                <CustomPicker
                    v-model="clone.workflowId"
                    :items="workflows"
                    idKey="_id"
                    labelKey="title"
                    placeholder="İş Akışı Seçin"
                />
            </section>

            <!-- Pipeline Stage Seçimi -->
            <section>
                <div class="title">Pipeline Aşaması</div>
                <CustomPicker
                    v-model="clone.pipelineStageId"
                    :items="filteredPipelineStages"
                    idKey="_id"
                    labelKey="name"
                    placeholder="Pipeline Aşaması Seçin"
                />
            </section>

            <!-- Başlık -->
            <section>
                <div class="title">Başlık</div>
                <TiptapEditor
                    placeholder="Görev Başlığı"
                    :show-attributes="true"
                    v-model="clone.title"
                    automation-mode
                />
            </section>

            <!-- Açıklama -->
            <section>
                <div class="title">Açıklama</div>
                <TiptapEditor
                    placeholder="Görev Açıklaması"
                    :show-attributes="true"
                    v-model="clone.description"
                    automation-mode
                />
            </section>

            <!-- Öncelik -->
            <section>
                <div class="title">Öncelik</div>
                <CustomPicker
                    v-model="clone.priority"
                    :items="priorityOptions"
                    idKey="value"
                    labelKey="label"
                    placeholder="Öncelik Seçin"
                />
            </section>

            <!-- Atananlar -->
            <section>
                <div class="title">Atananlar</div>
                <div v-for="(assignee, index) in clone.assigneeIds" :key="index" class="dynamic-field">
                    <div class="field-header">
                        <span>Atanan {{ index + 1 }}</span>
                        <div class="mode-switch">
                            <span class="switch-label">Dinamik</span>
                            <a-switch
                                size="small"
                                :checked="assignee.mode === 'dynamic'"
                                @change="(checked) => onModeChange(checked, assignee)"
                            />
                        </div>
                        <SupsisButton @click="removeAssignee(index)" icon danger xsmall>
                            <i class="material-icons">delete</i>
                        </SupsisButton>
                    </div>
                    <template v-if="assignee.mode === 'static'">
                        <CustomPicker
                            v-model="assignee.value"
                            :items="userOptions"
                            idKey="_id"
                            labelKey="name"
                            placeholder="Kullanıcı Seçin"
                        />
                    </template>
                    <template v-else>
                        <TiptapEditor
                            v-model="assignee.value"
                            placeholder="Dinamik Değer"
                            :show-attributes="true"
                            automation-mode
                        />
                    </template>
                </div>
                <a-button type="dashed" @click="addAssignee" block>
                    <a-icon type="plus" />
                    Atanan Ekle
                </a-button>
            </section>

            <!-- Takipçiler -->
            <section>
                <div class="title">Takipçiler</div>
                <div v-for="(follower, index) in clone.followers" :key="index" class="dynamic-field">
                    <div class="field-header">
                        <span>Takipçi {{ index + 1 }}</span>
                        <div class="mode-switch">
                            <span class="switch-label">Dinamik</span>
                            <a-switch
                                size="small"
                                :checked="follower.mode === 'dynamic'"
                                @change="(checked) => onFollowerModeChange(checked, follower)"
                            />
                        </div>
                        <SupsisButton @click="removeFollower(index)" icon danger xsmall>
                            <i class="material-icons">delete</i>
                        </SupsisButton>
                    </div>
                    <template v-if="follower.mode === 'static'">
                        <CustomPicker
                            v-model="follower.value"
                            :items="userOptions"
                            idKey="_id"
                            labelKey="name"
                            placeholder="Takipçi Seçin"
                        />
                    </template>
                    <template v-else>
                        <TiptapEditor
                            v-model="follower.value"
                            placeholder="Dinamik Değer"
                            :show-attributes="true"
                            automation-mode
                        />
                    </template>
                </div>
                <a-button type="dashed" @click="addFollower" block>
                    <a-icon type="plus" />
                    Takipçi Ekle
                </a-button>
            </section>

            <!-- İşbirlikçiler -->
            <section>
                <div class="title">İşbirlikçiler</div>
                <div v-for="(collaborator, index) in clone.collaborators" :key="index" class="dynamic-field">
                    <div class="field-header">
                        <span>İşbirlikçi {{ index + 1 }}</span>
                        <div class="mode-switch">
                            <span class="switch-label">Dinamik</span>
                            <a-switch
                                size="small"
                                :checked="collaborator.mode === 'dynamic'"
                                @change="(checked) => onCollaboratorModeChange(checked, collaborator)"
                            />
                        </div>
                        <SupsisButton @click="removeCollaborator(index)" icon danger xsmall>
                            <i class="material-icons">delete</i>
                        </SupsisButton>
                    </div>
                    <template v-if="collaborator.mode === 'static'">
                        <CustomPicker
                            v-model="collaborator.value"
                            :items="userOptions"
                            idKey="_id"
                            labelKey="name"
                            placeholder="İşbirlikçi Seçin"
                        />
                    </template>
                    <template v-else>
                        <TiptapEditor
                            v-model="collaborator.value"
                            placeholder="Dinamik Değer"
                            :show-attributes="true"
                            automation-mode
                        />
                    </template>
                </div>
                <a-button type="dashed" @click="addCollaborator" block>
                    <a-icon type="plus" />
                    İşbirlikçi Ekle
                </a-button>
            </section>

            <!-- Sahip -->
            <section>
                <div class="title">Sahip</div>
                <div class="dynamic-field">
                    <div class="field-header">
                        <div class="mode-switch">
                            <span class="switch-label">Dinamik</span>
                            <a-switch
                                size="small"
                                :checked="clone.ownerId.mode === 'dynamic'"
                                @change="(checked) => onOwnerModeChange(checked)"
                            />
                        </div>
                    </div>
                    <template v-if="clone.ownerId.mode === 'static'">
                        <CustomPicker
                            v-model="clone.ownerId.value"
                            :items="userOptions"
                            idKey="_id"
                            labelKey="name"
                            placeholder="Sahip Seçin"
                        />
                    </template>
                    <template v-else>
                        <TiptapEditor
                            v-model="clone.ownerId.value"
                            placeholder="Sahip ID'si gir"
                            :show-attributes="true"
                            automation-mode
                        />
                    </template>
                </div>
            </section>

            <!-- İletişim ID -->
            <section>
                <div class="title">Contact ID</div>
                <TiptapEditor
                    placeholder="Contact ID"
                    :show-attributes="true"
                    v-model="clone.contactId"
                    automation-mode
                />
            </section>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import { mapState } from "vuex";
import TiptapEditor from "../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import CustomPicker from "../../../../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../../../../Settings/components/SupsisButton.vue";

export default {
    name: "CreateTask",
    components: {
        TiptapEditor,
        CustomPicker,
        SupsisButton
    },
    props: ["value"],
    data() {
        return {
            clone: _.cloneDeep(this.value || {
                workflowId: null,
                pipelineStageId: null,
                title: "",
                description: "",
                priority: "low",
                assigneeIds: [],
                followers: [],
                collaborators: [],
                ownerId: { mode: "static", value: null },
                contactId: null
            }),
            priorityOptions: [
                { value: "lowest", label: "En Düşük" },
                { value: "low", label: "Düşük" },
                { value: "medium", label: "Orta" },
                { value: "high", label: "Yüksek" },
                { value: "highest", label: "En Yüksek" }
            ]
        };
    },
    computed: {
        ...mapState({
            workflows: state => state.tasks.workflows,
            users: state => state.user.users.data
        }),
        userOptions() {
            return this.users.map(user => ({
                _id: user._id,
                name: `${user.name} (${user.email})`
            }));
        },
        filteredPipelineStages() {
            if (!this.clone.workflowId) return [];
            const workflow = this.workflows.find(w => w._id === this.clone.workflowId);
            return workflow ? workflow.pipeline : [];
        }
    },
    methods: {
        addAssignee() {
            this.clone.assigneeIds.push({
                mode: "static",
                value: null
            });
        },
        removeAssignee(index) {
            this.clone.assigneeIds.splice(index, 1);
        },
        onModeChange(checked, item) {
            item.mode = checked ? "dynamic" : "static";
            item.value = null;
        },
        onOwnerModeChange(checked) {
            this.clone.ownerId.mode = checked ? "dynamic" : "static";
            this.clone.ownerId.value = null;
        },
        addFollower() {
            this.clone.followers.push({
                mode: "static",
                value: null
            });
        },
        removeFollower(index) {
            this.clone.followers.splice(index, 1);
        },
        onFollowerModeChange(checked, follower) {
            follower.mode = checked ? "dynamic" : "static";
            follower.value = null;
        },
        addCollaborator() {
            this.clone.collaborators.push({
                mode: "static",
                value: null
            });
        },
        removeCollaborator(index) {
            this.clone.collaborators.splice(index, 1);
        },
        onCollaboratorModeChange(checked, collaborator) {
            collaborator.mode = checked ? "dynamic" : "static";
            collaborator.value = null;
        }
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            }
        }
    }
};
</script>

<style lang="scss">
.block-editor.create-task {
    .wrapper {
        padding: 30px;

        section {
            margin-bottom: 20px;

            .title {
                margin-bottom: 10px;
                font-weight: 600;
                font-size: 16px;
                line-height: 20px;
            }
        }

        .dynamic-field {
            margin-bottom: 10px;
            padding: 10px;
            border: 1px solid #e8e8e8;
            border-radius: 4px;

            .field-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 10px;

                .mode-switch {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    .switch-label {
                        font-size: 12px;
                        color: rgba(0, 0, 0, 0.65);
                    }
                }
            }
        }
    }
}
</style>
